import { Injectable, inject, signal } from '@angular/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, catchError, of, tap } from 'rxjs';

/**
 * Runs `checkAuth()` exactly once, during bootstrap, and keeps its result.
 *
 * `checkAuth()` is the only entry point that rehydrates the session from
 * storage AND arms the library's periodic token check (internally
 * `startCheckSessionAndValidation`, which is what makes `silentRenew` /
 * `useRefreshToken` / `renewTimeBeforeTokenExpiresInSeconds` do anything). It
 * used to be called from `AuthCallbackComponent` only, so on every other entry
 * — a reload, a deep link, the full-page `window.location.href` navigations in
 * the sidebar/header — the session was never rehydrated and no token was ever
 * refreshed proactively: expiry surfaced as a 401 or as the route guard
 * hard-redirecting to the IdP.
 *
 * Angular runs app initializers before the router's initial navigation, so by
 * the time any guard or routed component runs, `result()` is populated.
 */
@Injectable()
export class AuthSessionService {
  private readonly oidc = inject(OidcSecurityService);

  private readonly _result = signal<LoginResponse | null>(null);

  /** The bootstrap `checkAuth()` outcome, or null if it has not run yet. */
  readonly result = this._result.asReadonly();

  initialize(): Observable<LoginResponse | null> {
    return this.oidc.checkAuth().pipe(
      catchError((error: unknown) =>
        of({
          isAuthenticated: false,
          errorMessage: String(error),
          userData: null,
          idToken: '',
          accessToken: '',
          configId: '',
        } satisfies LoginResponse),
      ),
      tap((result) => this._result.set(result)),
    );
  }
}
